/**
 * A friendly heads-up, shown once per visit, when someone opens one of the planning modules on a
 * phone.
 *
 * The client profile is a form and works fine on a phone — this is deliberately NOT shown there.
 * The modules are a different proposition: strategies compared side by side, charts, projections
 * and long tables of figures. They are legible on a laptop or tablet and a squeeze on a handset.
 *
 * ADVISORY, NEVER A BARRIER. Whoever is holding the phone may have good reason to be — sitting
 * with a parent, or away from a desk — so "Continue anyway" is the primary action and the notice
 * never returns for the rest of the session once answered. Blocking them would be presumptuous
 * about a device we cannot see the circumstances of.
 *
 * The cut is at 768px (Tailwind's `md`): tablets are on the recommended side of it, which matches
 * the advice the copy actually gives.
 */
import React, { useState, useEffect } from 'react';
import { Laptop, X } from 'lucide-react';

/** Answered once, remembered for the tab — asking again on each module would nag. */
const DISMISS_KEY = 'acw:smallScreenNoticeAnswered';

const answeredAlready = (): boolean => {
    try {
        return window.sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
        // Private browsing or blocked storage: better to show the notice than to crash on it.
        return false;
    }
};

const rememberAnswer = (): void => {
    try {
        window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
        /* Nothing to do — it will simply ask again next time. */
    }
};

interface SmallScreenNoticeProps {
    /** Where "Back to dashboard" goes. */
    onBack: () => void;
}

const SmallScreenNotice = ({ onBack }: SmallScreenNoticeProps) => {
    const [answered, setAnswered] = useState(answeredAlready);
    const [isPhone, setIsPhone] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
    );

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = (e: MediaQueryListEvent) => setIsPhone(e.matches);
        mq.addEventListener('change', onChange);
        setIsPhone(mq.matches);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    if (answered || !isPhone) return null;

    const answer = (then?: () => void) => {
        rememberAnswer();
        setAnswered(true);
        then?.();
    };

    return (
        <div
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="small-screen-notice-title"
        >
            <div className="absolute inset-0 bg-slate-900/50" aria-hidden="true" />
            <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                <button
                    type="button"
                    onClick={() => answer()}
                    aria-label="Close"
                    className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:bg-slate-100 hover:text-gray-600"
                >
                    <X size={18} aria-hidden="true" />
                </button>

                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-teal-400 shadow-lg shadow-blue-500/25">
                    <Laptop size={26} className="text-white" aria-hidden="true" />
                </div>

                <h2 id="small-screen-notice-title" className="text-xl font-bold text-gray-900 leading-snug">
                    This part is easier on a bigger screen
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    There&apos;s a fair bit to weigh up here — options compared side by side, charts, and
                    figures projected over several years. It all fits together much more clearly on a
                    laptop or an iPad.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    You&apos;re very welcome to carry on here if now is the right moment — nothing is
                    hidden on a phone.
                </p>

                <div className="mt-6 flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={() => answer()}
                        className="w-full min-h-[48px] rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-4 py-3 font-semibold text-white shadow-md"
                    >
                        Continue anyway
                    </button>
                    <button
                        type="button"
                        onClick={() => answer(onBack)}
                        className="w-full min-h-[48px] rounded-lg border border-slate-300 bg-white px-4 py-3 font-semibold text-gray-700"
                    >
                        Back to dashboard
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SmallScreenNotice;
